import * as THREE from "three";

import type { CameraPose } from "../player/controller";
import { type Daylight, makeDaylight, MORNING_PHASE, sampleDaylight, wrapPhase } from "./daylight";

/*
 * The scene: a sky dome, two lights, and a Z-up camera.
 *
 * Everything here is still deliberately plain. Gate A's art checkpoint bought
 * this file exactly three things — a vertical sky gradient, a fog colour matched
 * to that gradient's horizon band, and a light balance retuned against the
 * deeper ambient-occlusion curve in `crates/mesher/src/color.rs`
 * (`docs/plans/gate-a.md`, "Prototype 0.5"). What it did *not* buy is on the
 * same page: no water, no textures, no post-processing, no shadows. The one
 * material is still `MeshLambertMaterial` with vertex colours.
 *
 * The one structural decision is the up axis. The simulation's world is x/y
 * horizontal with z up (map §13.3.1) and the mesher emits vertices in that
 * frame; Three.js defaults to y up. Swizzling every vertex on the way in would
 * cost a pass over every buffer and would put a coordinate convention in two
 * places. Instead the *camera* is taught the sim's convention, once, here.
 *
 * # Phase 5: the rig moves, and the palette left
 *
 * Everything above was built once and never touched again — a fixed sun, a
 * dome with its gradient baked into its vertices at construction, and one
 * horizon colour shared by the dome's rim, the clear colour and the fog. Plan
 * decision 8 turns all of that into a function of the time of day, and the
 * *colours themselves* moved out to `render/daylight.ts` with it, because a
 * palette that has to be retuned by somebody looking at screenshots wants to be
 * one block in one file rather than seven constants among the geometry.
 *
 * What stays here is the rig and the two rules the colours are wired under, and
 * both are load-bearing:
 *
 * - **The rim, the clear colour and the fog are the same colour, always.** That
 *   is the rendering brief's rule (`docs/research/rendering/threejs-chunks.md`
 *   §3: "fog matched to the clear color so the streaming-ring edge
 *   disappears"), and it is why {@link SceneView.setTimeOfDay} writes the day's
 *   horizon into three places rather than one. A chunk that has not arrived is
 *   the colour of the sky it would have been drawn against — at night as much
 *   as at noon.
 * - **There are still exactly two lights.** The directional one is pointed at
 *   the sun by day and at the moon by night; nothing new is added, this phase
 *   or in the dark (`render/daylight.ts`).
 *
 * Terrain and props share one vertex-coloured Lambert material, so a light that
 * moves reaches every one of them for free — which is the whole reason this
 * pass costs nothing per object.
 */

/**
 * The elevation, as a sine, at which the sky has finished turning into
 * {@link SKY_ZENITH_COLOR}.
 *
 * The top of a 75-degree frame is only ~37 degrees above the horizon, so a
 * player looking level sees the bottom *half* of the sky and nothing else — a
 * gradient that spends itself between there and straight up is a gradient
 * nobody sees. Finishing at ~44 degrees puts the whole ramp inside the frame.
 *
 * The ramp itself is a smoothstep rather than the `pow(h, exponent)` three.js's
 * own sky example uses, because smoothstep is *flat at both ends*: the first few
 * degrees above the horizon stay the fog's colour, which is what keeps a
 * fog-swallowed ridge from standing out against the sky just above it.
 */
const SKY_ZENITH_AT = 0.7;

/**
 * The dome's radius, in view distances.
 *
 * Large enough to contain every chunk the streaming window keeps — the far
 * corner of the ring, 88 z-levels down, is about 1.8 view distances away — and
 * small enough to stay inside the camera's far plane, which is two. Both matter
 * because the sky is drawn *after* the world and depth-tested against it (see
 * {@link buildSkyDome}); a chunk beyond the dome would be hidden by it, and at
 * that distance the fog has already turned it into exactly the dome's colour.
 */
const SKY_RADIUS = 1.9;

/**
 * Segments around and up the sky dome.
 *
 * The colour is a function of height alone, so the ring count only has to be
 * high enough that the dome does not read as a polygon at the horizon, and the
 * band count is what the gradient's smoothness costs. 12 x 16 over a hemisphere
 * is 360 triangles and one draw call.
 */
const SKY_SEGMENTS = { around: 12, up: 16 };

/*
 * The colours the two lights and the dome used to hold — the hemisphere's warm
 * ground bounce, its flat-blue sky term, the warm sun, and the two intensities
 * Prototype 0.5 retuned so the sun took a larger share of the total and the
 * mesher's deeper AO curve showed in cavities — are now keyframes of the day
 * (`render/daylight.ts`). Every one of Gate A's numbers survived the move
 * unchanged and is what the palette's noon still is; the new work is the other
 * three quarters of the cycle.
 */

/**
 * How far the phase of day has to move before the frame is repainted.
 *
 * Not an optimisation of the lights — those are half a dozen field writes — but
 * of the *dome*, which is a couple of hundred vertex colours and a buffer
 * upload. A five-thousandth of a day is half a second of a twenty-minute one
 * and imperceptible in a palette whose narrowest segment is a tenth of a day
 * wide, so the sky turns twice a second while the world runs and every frame
 * while the preview control sweeps it.
 */
const PHASE_EPSILON = 1 / 2048;

/** Vertical field of view in degrees. */
const FIELD_OF_VIEW = 75;

/**
 * How completely the fog has swallowed the world at the view distance.
 *
 * `FogExp2`'s transmittance is `exp(-(density * z)^2)`, so a density of
 * `SATURATION / viewDistance` leaves `exp(-SATURATION^2)` of the far chunk
 * showing — two gives about 2%, which is "gone" without the hard edge a linear
 * fog's `far` plane makes when a chunk pops in just inside it. Exponential
 * squared rather than linear is the rendering brief's call
 * (`docs/research/rendering/threejs-chunks.md` §3, and the fixed-constraints
 * list in that directory's README).
 *
 * # Two, decided on screen (Phase 2 wave 4B)
 *
 * Wave 1B shipped this number without judging it and left the trade behind it
 * open, because it is a real one: heavier fog hides the streaming frontier, and
 * lighter fog lets a player read the mid-ground. Wave 4B settled it the only way
 * it can be settled, by looking — the same spawn vantage captured at 2 and at
 * 1.5, on the terraced world worldgen now produces.
 *
 * **At 1.5 the frontier shows.** `exp(-2.25)` is 10.5% rather than 1.8%, and
 * 10.5% of a chunk is enough: a lone slab at the edge of the ring reads as a
 * pale block standing against the sky, which is precisely the artifact the brief
 * asks fog to remove. The mid-ground gain that buys is real but small — the
 * terraces four chunks out are a little crisper — and it is not worth a hole in
 * the horizon. Ramps also changed the arithmetic since 1B: a terraced hillside
 * has far more silhouette than a smooth one, so the mid-ground now reads on its
 * geometry rather than on its contrast.
 */
const FOG_SATURATION = 2;

/** World up, in the simulation's frame. */
const UP = new THREE.Vector3(0, 0, 1);
const RIGHT = new THREE.Vector3(1, 0, 0);

/**
 * The rotation that takes a default Three.js camera (looking down -z, up +y) to
 * this world's frame (looking along +y, up +z). Composed with yaw and pitch on
 * every frame, so it is built once.
 */
const CAMERA_BASE_PITCH = Math.PI / 2;

/** Owns the renderer, the camera and the lights. The chunk registry adds the rest. */
export class SceneView {
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;

  private readonly renderer: THREE.WebGLRenderer;
  private readonly canvas: HTMLCanvasElement;

  /** The gradient the world is drawn against. Follows the camera; see {@link setCamera}. */
  private readonly sky: THREE.Mesh;

  /**
   * The dome's per-vertex elevation ramp, and the colour buffer it is painted
   * into.
   *
   * The gradient used to be baked once and never touched. It is now repainted
   * whenever the day moves far enough ({@link PHASE_EPSILON}), and the *ramp*
   * is what survives from the bake: the shape of the gradient is geometry and
   * never changes, so only the two colours it runs between do.
   */
  private readonly skyRamp: Float32Array;
  private readonly skyColors: Float32Array;
  private readonly skyAttribute: THREE.BufferAttribute;

  /** The two lights, kept so the day can point and colour them. */
  private readonly hemisphere: THREE.HemisphereLight;
  private readonly sun: THREE.DirectionalLight;

  /** The clear colour and the fog, kept for the same reason. */
  private readonly background = new THREE.Color();
  private readonly fog: THREE.FogExp2;

  /** Scratch, reused every frame rather than allocated per frame. */
  private readonly yawQuaternion = new THREE.Quaternion();
  private readonly pitchQuaternion = new THREE.Quaternion();
  private readonly daylight: Daylight = makeDaylight();
  private readonly shade = new THREE.Color();

  /** The phase the frame is currently lit for. `NaN` until the first one is applied. */
  private shownPhase = Number.NaN;

  constructor(canvas: HTMLCanvasElement, viewDistance: number) {
    this.canvas = canvas;

    // Set before any object exists: `Object3D.DEFAULT_UP` is copied into each
    // object at construction, so a light or camera made earlier would keep the
    // y-up default and quietly disagree with everything else.
    THREE.Object3D.DEFAULT_UP.copy(UP);

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    this.scene = new THREE.Scene();
    // The clear colour is the horizon band, and it is load-bearing: the dome
    // below is a *hemisphere*, so everything under the horizon that the world
    // does not cover is this clear, in exactly the colour the dome's rim and the
    // fog are. All three are filled in by `setTimeOfDay` below.
    this.scene.background = this.background;
    this.fog = new THREE.FogExp2(0x000000, FOG_SATURATION / viewDistance);
    this.scene.fog = this.fog;

    this.camera = new THREE.PerspectiveCamera(FIELD_OF_VIEW, 1, 0.1, viewDistance * 2);
    this.camera.up.copy(UP);

    const dome = buildSkyDome(viewDistance * SKY_RADIUS);
    this.sky = dome.mesh;
    this.skyRamp = dome.ramp;
    this.skyColors = dome.colors;
    this.skyAttribute = dome.attribute;
    this.scene.add(this.sky);

    // Both lights are built black and unlit and coloured by the first
    // `setTimeOfDay` below, so there is exactly one place in this file that
    // decides what the scene is lit by — a second copy of the palette here
    // would be a second thing to keep in step with `render/daylight.ts`.
    this.hemisphere = new THREE.HemisphereLight(0x000000, 0x000000, 0);
    // A hemisphere light points from its position towards the origin, so this is
    // "sky above" in the sim's frame rather than a place in the world.
    this.hemisphere.position.set(0, 0, 1);
    this.scene.add(this.hemisphere);

    this.sun = new THREE.DirectionalLight(0x000000, 0);
    this.scene.add(this.sun);

    // Morning, until something says otherwise: a session that has not watched a
    // day go by does not know the time and must not guess (`render/daylight.ts`).
    this.setTimeOfDay(MORNING_PHASE);

    this.resize();
  }

  /**
   * Draw calls in the last frame drawn.
   *
   * The number the rendering research says to measure rather than reason about
   * (`docs/research/rendering/threejs-chunks.md` §1: "~400 calls with a single
   * program is comfortably fine on desktop … measure with
   * `renderer.info.render.calls`", and the note that no credible public
   * benchmark exists). It resets itself at the start of every `render()`, so it
   * is read after one — which the HUD does.
   */
  get drawCalls(): number {
    return this.renderer.info.render.calls;
  }

  /** Triangles in the last frame drawn. The other half of the same measurement. */
  get triangles(): number {
    return this.renderer.info.render.triangles;
  }

  /**
   * Lights the scene for one moment of the day. Phase 0 is dawn
   * (`render/daylight.ts`).
   *
   * Called from the render phase, once a frame, with whatever the client
   * currently believes the time is — a measurement today, wave 3A's
   * `time_of_day()` when it lands, or the preview control's scrubber under
   * `?dev=1`. It is idempotent and it is cheap when nothing moved, which is
   * what lets `main.ts` call it unconditionally rather than deciding when the
   * sky is allowed to change.
   *
   * The order below is the order the frame reads it in: the sky the world sits
   * against, then the light on the world. The dome is repainted last because it
   * is the only part that costs anything ({@link PHASE_EPSILON}).
   */
  setTimeOfDay(phase: number): void {
    const wrapped = wrapPhase(phase);

    // A phase that has not moved is a frame with nothing to do. The comparison
    // is on the wrapped value, so the small hours rolling over into dawn is a
    // large difference and never mistaken for stillness.
    if (Math.abs(wrapped - this.shownPhase) < PHASE_EPSILON) {
      return;
    }

    this.shownPhase = wrapped;

    const light = sampleDaylight(wrapped, this.daylight);

    this.background.copy(light.horizon);
    this.fog.color.copy(light.horizon);

    this.hemisphere.color.copy(light.skyLight);
    this.hemisphere.groundColor.copy(light.groundLight);
    this.hemisphere.intensity = light.hemisphere;

    this.sun.color.copy(light.sun);
    this.sun.intensity = light.sunIntensity;
    // A directional light shines from its position towards its target, which
    // defaults to the origin — so this is a *direction* and its length does not
    // matter. The vector is already the unit one the arc produced.
    this.sun.position.copy(light.sunDirection);

    this.paintSky(light.horizon, light.zenith);
  }

  /** The phase the frame is lit for. The self-checks and the HUD read it. */
  get timeOfDay(): number {
    return this.shownPhase;
  }

  /** Points the camera. Yaw 0 looks along +y; positive pitch looks up. */
  setCamera(pose: CameraPose): void {
    this.camera.position.set(pose.x, pose.y, pose.z);

    // The dome is a backdrop, not a place: keeping it centred on the camera is
    // what makes its gradient a function of view *elevation* rather than of
    // where the player is standing, and it is also what stops a player who walks
    // far enough from reaching the edge of the sky.
    this.sky.position.copy(this.camera.position);
    this.sky.updateMatrix();

    // Rz(yaw) * Rx(pitch + 90 degrees), composed in that order: the pitch term
    // includes the base tilt that turns a y-up camera into a z-up one, so a
    // single multiply produces the whole orientation. Euler orders are avoided
    // here because which of the six a library means is never obvious from a call
    // site, and this is.
    this.yawQuaternion.setFromAxisAngle(UP, pose.yaw);
    this.pitchQuaternion.setFromAxisAngle(RIGHT, pose.pitch + CAMERA_BASE_PITCH);
    this.camera.quaternion.copy(this.yawQuaternion).multiply(this.pitchQuaternion);
  }

  /**
   * Matches the drawing buffer to the canvas's displayed size.
   *
   * Called every frame and does nothing on almost all of them. The size check is
   * not an optimisation: assigning `canvas.width` resets the drawing buffer even
   * when the value is unchanged, so an unconditional `setSize` would clear the
   * canvas once per frame.
   */
  resize(): void {
    const pixelRatio = Math.min(window.devicePixelRatio, 2);
    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;

    if (width === 0 || height === 0) {
      return;
    }

    const bufferWidth = Math.floor(width * pixelRatio);
    const bufferHeight = Math.floor(height * pixelRatio);

    if (this.canvas.width === bufferWidth && this.canvas.height === bufferHeight) {
      return;
    }

    this.renderer.setPixelRatio(pixelRatio);
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  /**
   * Runs the day's two sky colours down the dome's baked elevation ramp.
   *
   * The gradient's *shape* is geometry and was decided once at build time
   * ({@link buildSkyDome}); this is only the two ends of it moving. A couple of
   * hundred vertices and one buffer upload, gated on the phase actually having
   * moved — which is the difference between a sky that costs nothing and a sky
   * that re-uploads a buffer at frame rate to say the same thing.
   */
  private paintSky(horizon: THREE.Color, zenith: THREE.Color): void {
    for (let vertex = 0; vertex < this.skyRamp.length; vertex += 1) {
      this.shade.copy(horizon).lerp(zenith, this.skyRamp[vertex] ?? 0);
      this.skyColors[vertex * 3] = this.shade.r;
      this.skyColors[vertex * 3 + 1] = this.shade.g;
      this.skyColors[vertex * 3 + 2] = this.shade.b;
    }

    this.skyAttribute.needsUpdate = true;
  }
}

/** The dome, and the two buffers {@link SceneView.paintSky} works over. */
interface SkyDome {
  readonly mesh: THREE.Mesh;

  /** The smoothstep of each vertex's elevation, in 0..1. Baked once; never moves. */
  readonly ramp: Float32Array;

  /** The colour buffer the ramp is painted into, and the attribute that owns it. */
  readonly colors: Float32Array;
  readonly attribute: THREE.BufferAttribute;
}

/**
 * Builds the sky: a dome seen from the inside, coloured per vertex by height.
 *
 * Three ways to draw a gradient sky were on the table and this is the cheapest
 * one that is *correct in our frame*:
 *
 * - `scene.background` as a plain texture is drawn screen-aligned, so the
 *   gradient would not move when the player looks up.
 * - `scene.background` as an equirectangular texture does follow the view, but
 *   three.js maps it around **+y**, and this world's up is +z. The sky's poles
 *   would sit on the horizon.
 * - A dome with vertex colours needs no texture, no shader, no asset and no
 *   colour-space conversion at sample time: `THREE.Color` already stores
 *   linear-sRGB working values, which is exactly what a vertex colour is
 *   (research §3, and the same contract the mesher's buffers satisfy).
 *
 * A gradient costs a screen's worth of shaded pixels however it is drawn, and
 * this one is arranged to pay as little of that as it can: half a dome rather
 * than a sphere (see below), drawn **last** with depth testing on and depth
 * writing off, so it fills only what the world did not already cover. Both
 * matter more than they look: on the headless SwiftShader session this was
 * verified in, a naive sky-first sphere cost ~9 fps standing in a dug pit, and
 * what shipped costs ~1.5 ms a frame at spawn with the sky filling half the
 * screen. On a GPU, one flat-shaded fill of half a frame is not a number worth
 * writing down.
 *
 * Phase 5 splits the bake in two. The *ramp* — how far up the gradient each
 * vertex sits — is a fact about the geometry and is still computed once, here.
 * The two colours it runs between belong to the time of day and are painted in
 * afterwards ({@link SceneView.paintSky}), which is what turns a vertex-coloured
 * dome into a sky that can change colour without a shader or a texture.
 */
function buildSkyDome(radius: number): SkyDome {
  // A hemisphere, not a sphere: below the horizon the gradient is a constant,
  // and that constant is already on the screen as the clear colour. Half a dome
  // draws half the pixels for the same image, and the rim meets the clear colour
  // exactly because both are {@link SKY_HORIZON_COLOR}.
  const geometry = new THREE.SphereGeometry(
    radius,
    SKY_SEGMENTS.around,
    SKY_SEGMENTS.up,
    0,
    Math.PI * 2,
    0,
    Math.PI / 2,
  );

  // `SphereGeometry` puts its poles on +y, three.js's up; this world's is +z.
  // Baked into the vertices once here rather than carried as a rotation on the
  // object, so the mesh's matrix stays a translation that follows the camera and
  // the loop below can read a vertex's height straight off its z.
  geometry.rotateX(Math.PI / 2);

  const position = geometry.getAttribute("position");
  const colors = new Float32Array(position.count * 3);
  const ramp = new Float32Array(position.count);

  for (let vertex = 0; vertex < position.count; vertex += 1) {
    // The sine of the elevation angle, because the vertex is on a sphere of
    // known radius. The dome starts at the horizon, so this is never negative;
    // the clamp below is for the rim, where rounding could put it a hair under.
    const elevation = position.getZ(vertex) / radius;
    const climb = Math.min(Math.max(elevation, 0) / SKY_ZENITH_AT, 1);

    // Smoothstep, written out: `3t² - 2t³`.
    ramp[vertex] = climb * climb * (3 - 2 * climb);
  }

  const attribute = new THREE.BufferAttribute(colors, 3);
  geometry.setAttribute("color", attribute);
  // Normals and uvs are what `SphereGeometry` costs and this material never
  // reads: a basic material with vertex colours needs neither.
  geometry.deleteAttribute("normal");
  geometry.deleteAttribute("uv");

  const mesh = new THREE.Mesh(
    geometry,
    new THREE.MeshBasicMaterial({
      vertexColors: true,
      side: THREE.BackSide,
      // The sky is what the fog fades *to*; fogging it would fade it to itself
      // at the horizon and to a flat wash everywhere else.
      fog: false,
      depthWrite: false,
    }),
  );

  mesh.name = "sky";
  // After every chunk, and always visible: the camera is inside the dome, so
  // frustum culling has nothing to decide and its bounding sphere would only be
  // one more thing to keep in step with the camera.
  mesh.renderOrder = 1;
  mesh.frustumCulled = false;
  mesh.matrixAutoUpdate = false;

  return { mesh, ramp, colors, attribute };
}
